import type { SettingsTarget } from "@/components/settings/settings-navigation";
import type { TrayPanelRect } from "@/desktop/tray-panel-geometry";
import type { WindowOpener } from "@/desktop/window-opener";
export type SettingsRequest = {
  target: SettingsTarget;
  opener: WindowOpener;
  trayRect?: TrayPanelRect;
};
import { create } from "zustand";
import { invoke } from "@tauri-apps/api/core";
import { emit, listen } from "@tauri-apps/api/event";
import { getCurrentWindow } from "@tauri-apps/api/window";
import { track } from "@/desktop/observability";
import { useCanvasStore } from "@/stores/canvas-store";
import { isDesktopOverlayWindow } from "@/desktop/desktop-overlay-window";
import { routeSettingsRequestToDisplay } from "@/desktop/settings-display-request";
import {
  enterNativeDenkWindow,
  enterNativeDenkWorkspacePreviewWindow,
  exitNativeDenkWorkspacePreviewWindow,
  exitNativeWorkspaceWindow,
  syncNativeDenkCanvasCapture,
} from "@/desktop/native-workspace-geometry";
import {
  emitWorkspaceModeEvent,
  WORKSPACE_MODE_CLAIMED_EVENT,
  WORKSPACE_MODE_RELEASED_EVENT,
} from "@/lib/workspace-mode-events";

export type DesktopMode = "compact" | "peek" | "denk";
export type PeekReason = "hover" | "agent_frame_action" | "compact-reply";
export type DenkCanvasScope = "single-monitor" | "extended-desktop";
export type DenkCanvasFocusTarget = string[] | null;
type State = {
  mode: DesktopMode;
  /** Local frame ids rendered while the native window remains Compact. */
  compactFrameOverlayIds: string[];
  /** Whether the active frame overlay replaced an interactive Workspace. */
  compactFrameOverlayReturnsToWorkspace: boolean;
  nativeGeometryTransitioning: boolean;
  denkWorkspacePreview: boolean;
  denkCanvasCapture: boolean;
  denkCanvasScope: DenkCanvasScope;
  denkCanvasScopeSeq: number;
  denkCanvasFocusTarget: DenkCanvasFocusTarget;
  peekReason: PeekReason | null;
  /** Settings tab requested from outside the mounted App tree. */
  pendingSettingsRequest: SettingsRequest | null;
};

type Actions = {
  openWorkspace: () => void;
  openDenkMode: () => void;
  closeWorkspace: (
    frameOverlayIds?: string[],
    options?: { returnToWorkspace?: boolean },
  ) => void;
  clearCompactFrameOverlay: () => void;
  setDenkCanvasScope: (
    scope: DenkCanvasScope,
    focusTarget?: DenkCanvasFocusTarget,
  ) => void;
  beginDenkWorkspacePreview: () => void;
  endDenkWorkspacePreview: () => void;
  setDenkCanvasCapture: (active: boolean) => void;
  toggleWorkspace: () => void;
  openPeek: (reason: PeekReason) => void;
  closePeek: () => void;
  /** The person opened Settings (a click, a shortcut, a menu). */
  requestSettings: (
    target: SettingsTarget,
    trayRect?: TrayPanelRect,
    displayId?: number,
  ) => void;
  /** An agent, or Denker by itself, opened Settings: no keyboard. */
  revealSettings: (target: SettingsTarget, displayId?: number) => void;
  consumeSettingsRequest: () => SettingsRequest | null;
};

const DENK_CANVAS_SCOPE_EVENT = "denker://denk-canvas-scope";

let nativeWorkspaceTransitionSeq = 0;

function fireEnterDenk() {
  return enterNativeDenkWindow();
}

function fireEnterDenkWorkspacePreview() {
  return enterNativeDenkWorkspacePreviewWindow();
}

function fireExitDenkWorkspacePreview() {
  return exitNativeDenkWorkspacePreviewWindow();
}

function fireExitWorkspace() {
  return exitNativeWorkspaceWindow();
}

export function isExpandedMode(mode: DesktopMode): mode is "denk" {
  return mode === "denk";
}

function trackNativeGeometryTransition(promise: Promise<void>) {
  if (!isDesktopOverlayWindow()) return;
  const seq = ++nativeWorkspaceTransitionSeq;
  useDesktopMode.setState({ nativeGeometryTransitioning: true });
  void promise.finally(async () => {
    if (seq === nativeWorkspaceTransitionSeq) {
      useDesktopMode.setState({ nativeGeometryTransitioning: false });
    }
  });
}

function resetNativeWorkspaceOnLoad() {
  if (!isDesktopOverlayWindow()) return;
  void invoke("set_click_through", { enabled: true }).catch(() => {
    // Startup repair is best-effort. The JS click-through hook will
    // converge once React mounts.
  });
  trackNativeGeometryTransition(fireExitWorkspace());
  // A reload drops this window's Workspace without a close.
  emitWorkspaceModeEvent(WORKSPACE_MODE_RELEASED_EVENT);
}

function fireDenkCanvasScope(
  scope: DenkCanvasScope,
  focusTarget: DenkCanvasFocusTarget,
) {
  if (!isDesktopOverlayWindow()) return;
  void emit(DENK_CANVAS_SCOPE_EVENT, {
    scope,
    focusTarget,
    windowLabel: getCurrentWindow().label,
  }).catch(() => {});
}

function emitTransition(
  from: DesktopMode,
  to: DesktopMode,
  trigger: string,
  reason: PeekReason | null = null,
) {
  if (from === to) return;
  track("desktop_view_mode_changed", { from, to, trigger, reason });
}

function askForSettings(
  target: SettingsTarget,
  opener: WindowOpener,
  trayRect?: TrayPanelRect,
  displayId?: number,
) {
  if (!routeSettingsRequestToDisplay(target, opener, displayId))
    useDesktopMode.setState({
      pendingSettingsRequest: {
        target,
        opener,
        ...(trayRect ? { trayRect } : {}),
      },
    });
}

export const useDesktopMode = create<State & Actions>((set, get) => ({
  mode: "compact",
  compactFrameOverlayIds: [],
  compactFrameOverlayReturnsToWorkspace: false,
  nativeGeometryTransitioning: false,
  denkWorkspacePreview: false,
  denkCanvasCapture: false,
  denkCanvasScope: "single-monitor",
  denkCanvasScopeSeq: 0,
  denkCanvasFocusTarget: null,
  peekReason: null,
  pendingSettingsRequest: null,
  requestSettings: (target, trayRect, displayId) =>
    askForSettings(target, "user", trayRect, displayId),
  revealSettings: (target, displayId) =>
    askForSettings(target, "denker", undefined, displayId),
  consumeSettingsRequest: () => {
    const request = get().pendingSettingsRequest;
    if (request) set({ pendingSettingsRequest: null });
    return request;
  },
  openWorkspace: () => {
    get().openDenkMode();
  },
  openDenkMode: () => {
    const was = get().mode;
    const wasPreviewingDenk = was === "denk" && get().denkWorkspacePreview;
    const wasCapturingDenk = was === "denk" && get().denkCanvasCapture;
    const shouldEnterNative =
      isDesktopOverlayWindow() && (was !== "denk" || wasPreviewingDenk);
    set({
      mode: "denk",
      compactFrameOverlayIds: [],
      compactFrameOverlayReturnsToWorkspace: false,
      nativeGeometryTransitioning:
        shouldEnterNative || get().nativeGeometryTransitioning,
      denkWorkspacePreview: false,
      denkCanvasCapture: false,
      denkCanvasScope: "single-monitor",
      denkCanvasScopeSeq: get().denkCanvasScopeSeq + 1,
      denkCanvasFocusTarget:
        was === "compact" || was === "peek"
          ? get().denkCanvasFocusTarget
          : null,
      peekReason: null,
    });
    useCanvasStore.setState({
      isPanMode: false,
      commentModeActive: false,
      notePlacementActive: false,
    });
    if (was !== "denk") {
      emitWorkspaceModeEvent(WORKSPACE_MODE_CLAIMED_EVENT);
      trackNativeGeometryTransition(fireEnterDenk());
    } else if (wasPreviewingDenk) {
      trackNativeGeometryTransition(fireExitDenkWorkspacePreview());
    } else if (wasCapturingDenk) {
      trackNativeGeometryTransition(syncNativeDenkCanvasCapture(false));
    }
    emitTransition(was, "denk", "open");
  },
  closeWorkspace: (frameOverlayIds = [], options = {}) => {
    const was = get().mode;
    const compactFrameOverlayIds = [
      ...new Set(frameOverlayIds.filter(Boolean)),
    ];
    const current = get();
    const compactFrameOverlayReturnsToWorkspace =
      compactFrameOverlayIds.length > 0 &&
      (options.returnToWorkspace ??
        (current.compactFrameOverlayIds.length > 0
          ? current.compactFrameOverlayReturnsToWorkspace
          : false));
    const overlayUnchanged =
      current.compactFrameOverlayIds.length === compactFrameOverlayIds.length &&
      current.compactFrameOverlayIds.every(
        (id, index) => id === compactFrameOverlayIds[index],
      ) &&
      current.compactFrameOverlayReturnsToWorkspace ===
        compactFrameOverlayReturnsToWorkspace;
    if (
      was === "compact" &&
      overlayUnchanged &&
      !current.denkWorkspacePreview &&
      !current.denkCanvasCapture &&
      current.peekReason === null
    ) {
      return;
    }
    const shouldExitNative = isExpandedMode(was);
    set({
      mode: "compact",
      compactFrameOverlayIds,
      compactFrameOverlayReturnsToWorkspace,
      nativeGeometryTransitioning: shouldExitNative,
      denkWorkspacePreview: false,
      denkCanvasCapture: false,
      denkCanvasScope: "single-monitor",
      denkCanvasScopeSeq: get().denkCanvasScopeSeq + 1,
      denkCanvasFocusTarget:
        was === "denk" ? get().denkCanvasFocusTarget : null,
      peekReason: null,
    });
    useCanvasStore.setState({
      isPanMode: false,
      commentModeActive: false,
      notePlacementActive: false,
    });
    if (shouldExitNative) {
      trackNativeGeometryTransition(fireExitWorkspace());
      emitWorkspaceModeEvent(WORKSPACE_MODE_RELEASED_EVENT);
    }
    emitTransition(was, "compact", "close");
  },
  clearCompactFrameOverlay: () => {
    if (get().compactFrameOverlayIds.length === 0) return;
    set({
      compactFrameOverlayIds: [],
      compactFrameOverlayReturnsToWorkspace: false,
    });
  },
  setDenkCanvasScope: (scope, focusTarget = null) => {
    set((state) => ({
      denkCanvasScope: scope,
      denkCanvasScopeSeq: state.denkCanvasScopeSeq + 1,
      denkCanvasFocusTarget: focusTarget,
    }));
    fireDenkCanvasScope(scope, focusTarget);
  },
  beginDenkWorkspacePreview: () => {
    if (get().mode !== "denk" || get().denkWorkspacePreview) return;
    set({
      denkWorkspacePreview: true,
      nativeGeometryTransitioning: isDesktopOverlayWindow(),
    });
    trackNativeGeometryTransition(fireEnterDenkWorkspacePreview());
  },
  endDenkWorkspacePreview: () => {
    if (!get().denkWorkspacePreview) return;
    set({
      denkWorkspacePreview: false,
      nativeGeometryTransitioning:
        isDesktopOverlayWindow() && get().mode === "denk",
    });
    if (get().mode === "denk") {
      void fireExitDenkWorkspacePreview();
      trackNativeGeometryTransition(
        syncNativeDenkCanvasCapture(get().denkCanvasCapture),
      );
    }
  },
  setDenkCanvasCapture: (active) => {
    if (get().mode !== "denk") return;
    useCanvasStore.setState({
      isPanMode: active,
      commentModeActive: false,
      notePlacementActive: false,
    });
    if (get().denkCanvasCapture === active) return;
    set({ denkCanvasCapture: active });
    trackNativeGeometryTransition(
      syncNativeDenkCanvasCapture(active || get().denkWorkspacePreview),
    );
  },
  toggleWorkspace: () => {
    const was = get().mode;
    if (isExpandedMode(was)) {
      get().closeWorkspace();
      return;
    }
    get().openWorkspace();
  },
  openPeek: (reason) => {
    const was = get().mode;
    if (isExpandedMode(was) || get().compactFrameOverlayIds.length > 0) return;
    set({
      mode: "peek",
      compactFrameOverlayIds: [],
      compactFrameOverlayReturnsToWorkspace: false,
      denkWorkspacePreview: false,
      denkCanvasCapture: false,
      denkCanvasScope: "single-monitor",
      denkCanvasScopeSeq: get().denkCanvasScopeSeq + 1,
      denkCanvasFocusTarget: get().denkCanvasFocusTarget,
      peekReason: reason,
    });
    emitTransition(was, "peek", reason, reason);
  },
  closePeek: () => {
    const was = get().mode;
    if (was !== "peek") return;
    set({
      mode: "compact",
      denkWorkspacePreview: false,
      denkCanvasCapture: false,
      denkCanvasScope: "single-monitor",
      denkCanvasScopeSeq: get().denkCanvasScopeSeq + 1,
      denkCanvasFocusTarget: get().denkCanvasFocusTarget,
      peekReason: null,
    });
    emitTransition(was, "compact", "close-peek");
  },
}));

resetNativeWorkspaceOnLoad();

if (isDesktopOverlayWindow()) {
  void listen<{
    scope?: DenkCanvasScope;
    focusTarget?: DenkCanvasFocusTarget;
    windowLabel?: string;
  }>(DENK_CANVAS_SCOPE_EVENT, (event) => {
    const { scope, focusTarget = null, windowLabel } = event.payload;
    if (windowLabel === getCurrentWindow().label) return;
    if (scope !== "single-monitor" && scope !== "extended-desktop") return;
    useDesktopMode.setState((state) => ({
      denkCanvasScope: scope,
      denkCanvasScopeSeq: state.denkCanvasScopeSeq + 1,
      denkCanvasFocusTarget: focusTarget,
    }));
  }).catch(() => {});
}

if (typeof window !== "undefined") {
  window.addEventListener("pagehide", () => {
    if (
      isDesktopOverlayWindow() &&
      isExpandedMode(useDesktopMode.getState().mode)
    ) {
      const exitTransition = fireExitWorkspace();
      useDesktopMode.setState({
        mode: "compact",
        nativeGeometryTransitioning: true,
        denkWorkspacePreview: false,
        denkCanvasCapture: false,
        denkCanvasScope: "single-monitor",
        denkCanvasScopeSeq: useDesktopMode.getState().denkCanvasScopeSeq + 1,
        denkCanvasFocusTarget: null,
        peekReason: null,
      });
      trackNativeGeometryTransition(exitTransition);
    }
  });
}

// Dev-only HMR escape hatch. Without this, a Vite hot reload while the
// user is in workspace mode leaves them stranded — the OS window stays
// in workspace native state, the React tree re-mounts mid-state,
// and there's no clean path back to compact short of quitting Denker
// entirely. Disposing on every HMR tells Rust to restore the overlay
// rect (and revert the AppKit window level) BEFORE the new module
// replaces this one, so the next render starts in a known compact state
// and the user can click through to other apps immediately.
export function resetDesktopModeForHotReload() {
  try {
    if (!isDesktopOverlayWindow()) return;
    // Fail open on every HMR, not just workspace exits: the
    // teardown→remount gap has no click-through hook running, and a
    // stuck non-click-through fullscreen overlay eats all screen
    // clicks until the app is killed. The remounted hook re-decides.
    void invoke("set_click_through", { enabled: true }).catch(() => {});
    if (isExpandedMode(useDesktopMode.getState().mode)) {
      const exitTransition = fireExitWorkspace();
      useDesktopMode.setState({
        mode: "compact",
        nativeGeometryTransitioning: true,
        denkWorkspacePreview: false,
        denkCanvasCapture: false,
        denkCanvasScope: "single-monitor",
        denkCanvasScopeSeq: useDesktopMode.getState().denkCanvasScopeSeq + 1,
        denkCanvasFocusTarget: null,
        peekReason: null,
      });
      trackNativeGeometryTransition(exitTransition);
    }
  } catch {
    /* HMR cleanup must never throw */
  }
}

if (import.meta.hot) {
  import.meta.hot.dispose(resetDesktopModeForHotReload);
}
